import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * LA PÁGINA EN BLANCO DE DESPUÉS DE UN DESPLIEGUE, Y SU RED DE SEGURIDAD.
 *
 * ── QUÉ PASÓ, MEDIDO EL 2026-09-08 ─────────────────────────────────────────
 * La usuaria abrió «Prospecto Nuevo Cliente» y le salió la pantalla **en
 * blanco**: ni el menú, ni la cabecera, ni un mensaje. En su navegador:
 *
 *   Failed to load module script: Expected a JavaScript-or-Wasm module script
 *   but the server responded with a MIME type of "text/html".
 *
 * Las 68 páginas del portal se cargan con `lazy(() => import(...))`, y Vite le
 * pone un HASH a cada trozo (`Prospecto-DllbhXZE.js`). Cada despliegue cambia
 * el hash de lo que cambió. Una pestaña que lleva un rato abierta conserva el
 * `index.html` ANTERIOR, así que cuando la persona entra en una página que
 * todavía no había abierto, el navegador pide un trozo que **ya no existe** —y
 * Netlify, por el catch-all de `netlify.toml`, contesta el `index.html` con
 * `200 text/html` en vez de un 404—. El navegador rechaza el módulo por el MIME
 * y el `import()` queda rechazado.
 *
 * Medido ese día sobre los tres trozos de esa misma página:
 *   Prospecto-CWEsz2wA.js  → 200 **text/html**          (build de antes)
 *   Prospecto-DllbhXZE.js  → 200 **text/html**          (el que su pestaña pedía)
 *   Prospecto-DY8F4RwW.js  → 200 application/javascript (el vivo)
 *
 * ── POR QUÉ SALÍA EN BLANCO Y NO UN ERROR ──────────────────────────────────
 * Porque el portal **no tenía ni un `ErrorBoundary`**. Un `lazy()` que rechaza
 * lanza dentro del `Suspense`, y sin nadie que lo recoja React desmonta el
 * árbol ENTERO: no queda ni el marco. Por eso el síntoma es «no carga nada» en
 * vez de «esta sección falló».
 *
 * Y no es un problema de esta página ni de este despliegue: le pasa a
 * CUALQUIERA que tenga el portal abierto cuando entra una versión nueva, en
 * cualquiera de las 68 páginas. Solo se nota en la que cambió de hash.
 *
 * ── LAS DOS MITADES DEL ARREGLO ────────────────────────────────────────────
 * 1. `cargarPagina()` envuelve cada `import()`: si falla, **recarga la página
 *    una sola vez**, que es lo que trae el `index.html` nuevo y con él los
 *    hashes buenos. Es lo mismo que haría un Ctrl+F5, hecho solo.
 * 2. `LimiteDeError` recoge lo que llegue igual —una segunda vez, o un error
 *    de verdad de la página— y pinta un aviso con un botón. Un mensaje feo es
 *    infinitamente mejor que una pantalla vacía: la vacía no se sabe si es el
 *    portal, la red o el equipo.
 *
 * ── Y EL AVISO NO PUEDE DAR LA CAUSA POR SABIDA (usuaria, 2026-09-08) ───────
 * El texto del primer día decía «suele pasar cuando el portal se actualizó
 * mientras lo tenías abierto: vuelve a cargar y debería entrar», y ese mismo día
 * dejó a la usuaria en un callejón: no estaba dentro de la sección cuando entró
 * la versión nueva, recargó con Ctrl+F5 y el aviso seguía ahí. No era el
 * despliegue —era un borrador viejo en `localStorage` que tumbaba el formulario
 * en cada carga (ver `encajarFicha` en `paginas/Prospecto.tsx`)—, así que el
 * aviso la mandaba a hacer justo lo único que no podía funcionar.
 *
 * Este boundary NO sabe por qué falló: le llega un error de render, y el
 * despliegue es la causa más FRECUENTE, no la única. Así que ahora el texto
 * ordena las dos: recarga primero, y si ya recargaste, **no es eso** y hay que
 * avisar. Y cuando hay trabajo guardado en el equipo —hoy solo el borrador de
 * la ficha de Prospecto— ofrece la única salida que una recarga no da.
 */

/** Marca de la última recarga automática. `sessionStorage` y no `local`: es del
 *  navegador y de ESTA pestaña, y no tiene por qué sobrevivir a cerrarla. */
const CLAVE = 'pm:recarga-por-version';
/** Dos recargas seguidas en menos de esto son un BUCLE, no un despliegue. */
const VENTANA_MS = 30_000;

/** ¿Podemos recargar, o ya lo intentamos hace nada? */
function puedeRecargar(): boolean {
  try {
    const previo = Number(sessionStorage.getItem(CLAVE) ?? 0);
    if (previo && Date.now() - previo < VENTANA_MS) return false;
    sessionStorage.setItem(CLAVE, String(Date.now()));
    return true;
  } catch {
    /* Modo privado o sin cuota: sin memoria no se puede evitar el bucle, así
     * que NO se recarga. Mejor el aviso del `LimiteDeError` que una pestaña
     * recargándose sola para siempre. */
    return false;
  }
}

/**
 * Envuelve el `import()` de una página. Si el trozo no se puede traer —el caso
 * del despliegue nuevo— recarga una vez; si no, deja que el error siga su
 * camino hasta el `LimiteDeError`.
 *
 * NO se intenta distinguir «el trozo no existe» de «no hay red» por el texto
 * del error: cada navegador lo redacta distinto («Failed to fetch dynamically
 * imported module» en Chrome, «Importing a module script failed» en Safari,
 * «error loading dynamically imported module» en Firefox) y acertar con los
 * tres es más frágil que recargar. Sin red, la recarga tampoco carga y el
 * candado de los 30 s impide que se repita.
 */
export function cargarPagina<T>(importar: () => Promise<T>): Promise<T> {
  return importar().catch((e: unknown) => {
    if (puedeRecargar()) {
      /* `location.reload()` conserva la URL, así que la persona vuelve a la
       * misma página ya con la versión nueva. La promesa se deja colgada a
       * propósito: la pestaña se está yendo, y resolverla pintaría un parpadeo
       * de error justo antes de recargar. */
      window.location.reload();
      return new Promise<T>(() => {});
    }
    throw e;
  });
}

/**
 * Las ranuras de `localStorage` donde el portal guarda TRABAJO A MEDIAS. Hoy
 * hay una sola —`pm:prospecto:borrador:v1:<usuario>`— y por eso se buscan por
 * su forma y no por una lista: la que se añada mañana entra sola, y nada más
 * del navegador se toca.
 *
 * Se mira para NO ofrecer un botón destructivo cuando no hay nada que
 * descartar: sin borradores guardados, la única salida honesta es recargar.
 */
export function borradoresGuardados(): string[] {
  try {
    return Object.keys(localStorage)
      .filter((k) => k.startsWith('pm:') && k.includes('borrador'));
  } catch {
    return []; /* modo privado: como si no hubiera ninguno */
  }
}

interface Estado { fallo: Error | null }

/**
 * La red de abajo del todo. Recoge cualquier error de render del portal —no
 * solo los de carga— y enseña de qué sección fue, en vez de dejar el blanco.
 */
export class LimiteDeError extends Component<{ children: ReactNode }, Estado> {
  override state: Estado = { fallo: null };

  static getDerivedStateFromError(error: Error): Estado {
    return { fallo: error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    /* A la consola con el árbol de componentes: es lo que hace falta para
     * diagnosticar, y es lo único que se puede dejar sin sesión. */
    console.error('El portal falló al pintar:', error, info.componentStack);
  }

  override render() {
    const { fallo } = this.state;
    if (!fallo) return this.props.children;
    const borradores = borradoresGuardados();

    /* El estilo de los dos botones, para que se lean como lo que son: el de
     * recargar es la acción normal; el de descartar, la última opción. */
    const boton = {
      padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
      font: '600 13px system-ui, Arial, sans-serif',
    } as const;

    /* Sin clases del tema: si lo que falló fue la hoja de estilos, un aviso que
     * dependa de ella tampoco se vería. */
    return (
      <div style={{
        margin: '2rem auto', maxWidth: '34rem', padding: '1.25rem 1.5rem',
        border: '1px solid #e0c3c3', borderRadius: 12, background: '#fdf6f6',
        color: '#5a2020', font: '14px/1.6 system-ui, Arial, sans-serif',
      }}>
        <p style={{ margin: 0, fontWeight: 700 }}>Esta sección no se pudo abrir</p>
        <p style={{ margin: '8px 0 0' }}>
          Vuelve a cargar la página. Si el portal se actualizó mientras lo tenías abierto
          —que es lo más frecuente—, con eso entra.
        </p>
        <p style={{ margin: '8px 0 0' }}>
          <b>Si ya recargaste y sigue igual, no es la actualización</b>: es un fallo de esta
          sección, y recargar no lo va a arreglar. Avísale a C.E.I. con el detalle de aquí
          abajo y con lo que estabas haciendo.
        </p>
        <p style={{ margin: '10px 0 0', fontSize: 12, opacity: 0.75 }}>
          Detalle técnico: {fallo.message || String(fallo)}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 14 }}>
          <button
            type="button"
            onClick={() => {
              /* Se limpia la marca para que esta recarga no cuente como el
               * intento automático: la persona la está pidiendo a mano. */
              try { sessionStorage.removeItem(CLAVE); } catch { /* sin cuota */ }
              window.location.reload();
            }}
            style={{ ...boton, border: 0, background: '#8a2020', color: '#fff' }}
          >
            Volver a cargar
          </button>
          {borradores.length > 0 && (
            <button
              type="button"
              onClick={() => {
                for (const k of borradores) {
                  try { localStorage.removeItem(k); } catch { /* privado */ }
                }
                try { sessionStorage.removeItem(CLAVE); } catch { /* sin cuota */ }
                window.location.reload();
              }}
              style={{ ...boton, border: '1px solid #b98080', background: '#fff', color: '#5a2020' }}
            >
              Descartar el borrador y volver a cargar
            </button>
          )}
        </div>
        {borradores.length > 0 && (
          <p style={{ margin: '10px 0 0', fontSize: 12, opacity: 0.75 }}>
            Lo segundo, solo si lo primero no sirvió: tira la ficha a medio llenar que este
            equipo tenga guardada —un borrador de una versión anterior del formulario puede
            trabar la sección en cada carga— y eso no se puede deshacer.
          </p>
        )}
      </div>
    );
  }
}
